// Grouped Variant Selector
// Handles mutually exclusive variant groups

if (!customElements.get('variant-groups')) {
  customElements.define('variant-groups', class VariantGroups extends HTMLElement {
    constructor() {
      super();
      this.masterSelect = this.querySelector('.variant-group-selector');
      this.variantSections = this.querySelectorAll('[data-variant-group]');
      
      if (this.masterSelect) {
        this.masterSelect.addEventListener('change', this.onGroupChange.bind(this));
        // Initialize - show first group by default
        this.onGroupChange();
      }
    }

    onGroupChange() {
      const selectedGroup = this.masterSelect.value;
      
      // Hide all variant sections
      this.variantSections.forEach(section => {
        section.style.display = 'none';
        // Uncheck all radios in hidden sections
        const radios = section.querySelectorAll('input[type="radio"]');
        radios.forEach(radio => radio.checked = false);
      });
      
      // Show selected group
      if (selectedGroup) {
        const activeSection = this.querySelector(`[data-variant-group="${selectedGroup}"]`);
        if (activeSection) {
          activeSection.style.display = 'block';
          
          // Auto-select first option in the visible group
          const firstRadio = activeSection.querySelector('input[type="radio"]');
          if (firstRadio) {
            firstRadio.checked = true;
            // Trigger change event to update variant
            const changeEvent = new Event('change', { bubbles: true });
            firstRadio.dispatchEvent(changeEvent);
          }
        }
      }
    }
  });
}

// Extended VariantRadios to work with grouped variants
class GroupedVariantRadios extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange(event) {
    // Get the variant ID directly from the selected radio button
    const checkedRadio = event.target.closest('.variant-group-section')?.querySelector('input[type="radio"]:checked');
    
    if (checkedRadio && checkedRadio.dataset.variantId) {
      // Direct variant selection
      const variantId = parseInt(checkedRadio.dataset.variantId);
      this.currentVariant = this.getVariantData().find(variant => variant.id === variantId);
    } else {
      // Fallback to option-based matching
      this.updateOptions();
      this.updateMasterId();
    }

    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    // Only get checked radios from visible sections
    const visibleSections = Array.from(this.querySelectorAll('[data-variant-group]'))
      .filter(section => section.style.display !== 'none');
    
    this.options = [];
    visibleSections.forEach(section => {
      const fieldsets = Array.from(section.querySelectorAll('fieldset'));
      fieldsets.forEach((fieldset) => {
        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          this.options.push(checkedRadio.value);
        }
      });
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      // Match variant by checking if all selected options match
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    mediaGalleries.forEach(mediaGallery => {
      if (mediaGallery.setActiveMedia) {
        mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);
      }
    });

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    if (newMediaModal) modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (input) {
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(`price-${sectionId}`);
        if (source && destination) destination.innerHTML = source.innerHTML;

        const price = document.getElementById(`price-${this.dataset.section}`);
        if (price) price.classList.remove('visibility-hidden');
        
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    if (!button) return;
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('grouped-variant-radios', GroupedVariantRadios);
